'use client';

import React, { useState, useEffect } from 'react';

interface CountdownTimerProps {
  targetDate: string;
}

interface TimeRemaining {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const CountdownTimer = ({ targetDate }: CountdownTimerProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<TimeRemaining>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    const calculateTimeRemaining = () => {
      const target = new Date(targetDate).getTime();
      const now = new Date().getTime();
      const difference = target - now;

      if (difference > 0) {
        setTimeRemaining({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((difference % (1000 * 60)) / 1000),
        });
      } else {
        setTimeRemaining({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };

    calculateTimeRemaining();
    const interval = setInterval(calculateTimeRemaining, 1000);

    return () => clearInterval(interval);
  }, [targetDate, isHydrated]);

  if (!isHydrated) {
    return (
      <section className="py-16 px-4 bg-surface">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="font-playfair text-3xl sm:text-4xl font-semibold text-foreground mb-12">
            Counting Down to Our Special Day
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-card rounded-lg p-8 shadow-sacred-md">
                <div className="font-playfair text-5xl font-bold text-primary mb-2">--</div>
                <div className="font-inter text-sm uppercase tracking-wider text-muted">Loading</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  const timeUnits = [
    { value: timeRemaining.days, label: 'Days' },
    { value: timeRemaining.hours, label: 'Hours' },
    { value: timeRemaining.minutes, label: 'Minutes' },
    { value: timeRemaining.seconds, label: 'Seconds' },
  ];

  return (
    <section className="py-16 px-4 bg-surface">
      <div className="max-w-6xl mx-auto text-center">
        <h2 className="font-playfair text-3xl sm:text-4xl font-semibold text-foreground mb-4">
          Counting Down to Our Special Day
        </h2>
        <p className="font-inter text-base text-muted-foreground mb-12 max-w-2xl mx-auto">
          Every moment brings us closer to the day when two hearts become one in the presence of God and our beloved community.
        </p>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {timeUnits.map((unit, index) => (
            <div
              key={unit.label}
              className="bg-card rounded-lg p-8 shadow-sacred-md hover:shadow-sacred-lg transition-shadow duration-300"
            >
              <div className="font-playfair text-5xl sm:text-6xl font-bold text-primary mb-2">
                {String(unit.value).padStart(2, '0')}
              </div>
              <div className="font-inter text-sm uppercase tracking-wider text-muted">
                {unit.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CountdownTimer;